import React, { useEffect, useState } from 'react';
import { Clock, Video, MapPin, CheckCircle2 } from 'lucide-react';
import { meetingService, Meeting } from '../../../services/meeting.service.ts';
import { isMeetingLiveActive } from '../../meeting/utils/meetingLiveExpiry.ts';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function getMeetingEndTime(meeting: Meeting): number | null {
  if (meeting.endedAt) return new Date(meeting.endedAt).getTime();
  if (meeting.scheduledAt) {
    return new Date(meeting.scheduledAt).getTime() + (meeting.duration || 60) * 60_000;
  }
  if (meeting.startedAt) {
    return new Date(meeting.startedAt).getTime() + (meeting.duration || 60) * 60_000;
  }
  return null;
}

// Son 1 hafta içinde yapılmış / süresi geçmiş toplantılar
function isRecentPastMeeting(meeting: Meeting, now: number): boolean {
  if (isMeetingLiveActive(meeting)) return false;
  const endTime = getMeetingEndTime(meeting);
  if (endTime == null) return false;
  return endTime < now && now - endTime <= WEEK_MS;
}

function formatRelativeDay(timestamp: number, now: number): string {
  const dayMs = 24 * 60 * 60 * 1000;
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((startOfToday.getTime() - timestamp) / dayMs) + 1;

  if (timestamp >= startOfToday.getTime()) return 'Bugün';
  if (diffDays <= 1) return 'Dün';
  return `${diffDays} gün önce`;
}

const RecentActivity: React.FC = () => {
  const [pastMeetings, setPastMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const meetings = await meetingService.getMeetings();
        if (cancelled) return;
        const now = Date.now();
        const recent = meetings
          .filter((m) => isRecentPastMeeting(m, now))
          .sort((a, b) => (getMeetingEndTime(b) ?? 0) - (getMeetingEndTime(a) ?? 0))
          .slice(0, 6);
        setPastMeetings(recent);
      } catch (error) {
        console.error('Son etkinlikler yüklenemedi:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const now = Date.now();

  return (
    <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Son Etkinlik</h3>
        <span className="text-xs text-gray-500">Son 7 gün</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
        </div>
      ) : pastMeetings.length === 0 ? (
        <div className="text-center py-8">
          <Video className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">Son 1 haftada tamamlanan toplantı yok</p>
        </div>
      ) : (
        <div className="space-y-2">
          {pastMeetings.map((meeting) => {
            const endTime = getMeetingEndTime(meeting) ?? now;
            const isInPerson = meeting.meetingFormat === 'IN_PERSON';
            const timeLabel = new Date(
              meeting.scheduledAt || meeting.startedAt || endTime
            ).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

            return (
              <div
                key={meeting.id}
                className="flex items-center gap-3 p-3 bg-white/5 rounded-lg border border-transparent hover:border-white/10 transition"
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isInPerson ? 'bg-green-500/15 text-green-400' : 'bg-purple-500/15 text-purple-400'
                }`}>
                  {isInPerson ? <MapPin className="w-4 h-4" /> : <Video className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{meeting.title}</p>
                  <p className="text-gray-400 text-xs flex items-center gap-1.5 flex-wrap">
                    <span>{formatRelativeDay(endTime, now)}</span>
                    <span className="text-gray-600">•</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {timeLabel} – {meeting.duration} dk
                    </span>
                  </p>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 flex-shrink-0">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500/70" />
                  <span className="hidden sm:inline">Tamamlandı</span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RecentActivity;
